import { Component, OnInit, Input } from "@angular/core";
import { ModuleManager } from '../../../../shared/models/module-manager';
import SlideoutSettingsManager from "../../../../shared/models/slideout-settings-manager";
import { ModuleManagerDateRangeInfo } from "../../../../shared/models/module-manager/module-manager-settings.models";
import { MetricReviewService } from "../../modules/metric-review/metric-review.service";
import { CVLocalizationService } from "../../../reusables/services/cv-localization.service";

@Component({
    selector: "cv-metric-review-settings",
    templateUrl: "./metric-review-settings.component.html",
    styleUrls: [],
})
export class MetricReviewSettingsComponent implements OnInit {

    @Input() moduleManager: ModuleManager;
    @Input() settingsManager: SlideoutSettingsManager;

    moduleKey: string;
    
    datesToCompare = {
        firstDate: new ModuleManagerDateRangeInfo(),
        secondDate: new ModuleManagerDateRangeInfo(),
        thirdDate: new ModuleManagerDateRangeInfo(),
    }

    initialized = false;
    availableDateRanges: ModuleManagerDateRangeInfo[];
    dropdownOnly = false;
    today: Date = new Date();

    superDateRange: string; //this will encompass all 3 date ranges for group selector filtering

    constructor(
        private readonly service: MetricReviewService,
        private readonly cvLocalizationService: CVLocalizationService,
    ) {}

    ngOnInit(){
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
        this.cvLocalizationService.GetSection("Controls");

        this.moduleKey = this.moduleManager.getModuleKey();

        this.availableDateRanges = await this.service.getAvailableDateRanges(this.moduleKey);
        this.availableDateRanges = this.availableDateRanges?.map(dr => ModuleManagerDateRangeInfo.fromObject(dr));

        // set default value for date range selectors
        const firstDefault = ModuleManagerDateRangeInfo.fromObject({
            name: 'Today',
            displayName: 'Today',
            utcOffset: 0,
        });
        const secondDefault = ModuleManagerDateRangeInfo.fromObject({
            name: 'Last 7 Days',
            displayName: 'Last 7 Days',
            utcOffset: 0,
        });
        const thirdDefault = ModuleManagerDateRangeInfo.fromObject({
            name: 'Last 30 Days',
            displayName: 'Last 30 Days',
            utcOffset: 0,
        });

        this.moduleManager.addSetting({
            name: "CustomDateRanges",
            getValue: () => this.datesToCompare,
            onSettingLoaded: (value) => {
                this.datesToCompare = {
                    firstDate: ModuleManagerDateRangeInfo.fromObject(value.firstDate),
                    secondDate: ModuleManagerDateRangeInfo.fromObject(value.secondDate),
                    thirdDate: ModuleManagerDateRangeInfo.fromObject(value.thirdDate),
                };
            },
            defaultValue: {
                firstDate: firstDefault,
                secondDate: secondDefault,
                thirdDate: thirdDefault,
            },
        });

        this.moduleManager.customSettingsAdded = true;
        this.initialized = true;
    }

    onDateChanged(dateChanged: ModuleManagerDateRangeInfo) {
        if (dateChanged.displayName === "Custom..." || dateChanged.name.includes(" - ")) {
            //custom date range was selected
            dateChanged.displayName = dateChanged.name;
        }
   
        this.determineSuperDate();
    }

    getDateLocaleText(preset) {
        let key = preset.replace(/\s(?=15)/g, "").replace(/\s+/g, '_');
        return this.cvLocalizationService.getCachedText("Controls", key, preset);
    }

    determineSuperDate() {
        // Update super date to encompass all 3 date ranges
        const starts = [
            this.datesToCompare.firstDate?.startDate,
            this.datesToCompare.secondDate?.startDate,
            this.datesToCompare.thirdDate?.startDate
        ].filter(Boolean);
        const ends = [
            this.datesToCompare.firstDate?.endDate,
            this.datesToCompare.secondDate?.endDate,
            this.datesToCompare.thirdDate?.endDate
        ].filter(Boolean);

        // Find the earliest start and latest end
        const earliestDate = new Date(Math.min(...starts.map(d => d.getTime())));
        const latestDate = new Date(Math.max(...ends.map(d => d.getTime())));

        this.superDateRange = this.formatDate(earliestDate) + " - " + this.formatDate(latestDate);
    }

    formatDate(date: Date): string {
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
    }
}

